import { Component, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { PersonNameService } from '../../../../../security/services/person-name.service';
import { SharedToasterService } from '../../../../../shared/services/shared-toaster.service';
import { AssignmentParticipantsAttendanceDateModel } from '../../models/assignment-participants-attendances/assignment-participants-attendance-date.model';
import { AssignmentParticipantsAttendancesUpdatePostModel } from '../../models/assignment-participants-attendances/assignment-participants-attendances-update-post.model';
import { AssignmentParticipantsAttendancesModel } from '../../models/assignment-participants-attendances/assignment-participants-attendances.model';
import { AssignmentParticipantAttendanceStateEnum } from '../../models/assignment-participants-attendances/enums/assignment-participant-attendance-state.enum';
import { AssignmentParticipantsAttendanceService } from '../../services/assignment-participants-attendance.service';

@Component({
    templateUrl: './assignment-attendance-modal.component.html',
    styleUrls: ['./assignment-attendance-modal.component.scss'],
})
export class AssignmentAttendanceModalComponent implements OnInit {
    @Input({ required: true }) assignmentId!: number;
    @Input() isReadonly: boolean = false;

    protected isSaving: boolean = false;
    protected dates: AssignmentParticipantsAttendanceDateModel[] | null = null;
    protected participantsAttendances: AssignmentParticipantsAttendancesModel[] | null = null;
    protected isLoading: boolean = false;

    protected attendanceStateEnum = AssignmentParticipantAttendanceStateEnum;

    constructor(
        private assignmentParticipantsAttendanceService: AssignmentParticipantsAttendanceService,
        private sharedToasterService: SharedToasterService,
        private bsModalRef: BsModalRef,
        protected personNameService: PersonNameService
    ) { }

    ngOnInit(): void {
        this.loadParticipantsAttendances();
    }

    protected changeToNextState(event: Event, attendanceDateModel: AssignmentParticipantsAttendanceDateModel): void {
        // this is needed to prevent default focus behavior
        event.preventDefault();
        attendanceDateModel.state = this.getNextState(attendanceDateModel.state);
    }

    protected setRestAsAttended(eventLessonId: number) {
        this.setRestToState(eventLessonId, AssignmentParticipantAttendanceStateEnum.Attended);
    }

    protected setRestAsNotAttended(eventLessonId: number) {
        this.setRestToState(eventLessonId, AssignmentParticipantAttendanceStateEnum.Absent);
    }

    protected isAnyAttendanceSet(eventLessonId: number): boolean {
        if (this.participantsAttendances == null) return false;

        let anySet = false;

        this.participantsAttendances.forEach(x => {
            var date = x.dates.find(
                d => d.eventLessonId == eventLessonId && d.state != AssignmentParticipantAttendanceStateEnum.Undefined
            );
            if (date != null) anySet = true;
        });

        return anySet;
    }

    protected getParticipantsCountByState(eventLessonId: number, state: AssignmentParticipantAttendanceStateEnum): number {
        if (this.participantsAttendances == undefined || this.participantsAttendances == null) return 0;

        let count = 0;

        this.participantsAttendances.forEach(x => {
            let date = x.dates.find(d => d.eventLessonId == eventLessonId);
            if (date?.state == state) count++;
        })

        return count;
    }

    private setRestToState(eventLessonId: number, state: AssignmentParticipantAttendanceStateEnum) {
        if (this.participantsAttendances == null) return;

        this.participantsAttendances.forEach(x => {
            var date = x.dates.find(d => d.eventLessonId == eventLessonId && d.state == AssignmentParticipantAttendanceStateEnum.Undefined);
            if (date == null) return;

            date.state = state;
        });
    }

    protected isAttendanceDateUndefined(attendanceDateModel: AssignmentParticipantsAttendanceDateModel): boolean {
        if (attendanceDateModel.state == this.attendanceStateEnum.Undefined) return true;
        return false;
    }

    protected isAttendanceDateAttended(attendanceDateModel: AssignmentParticipantsAttendanceDateModel): boolean {
        if (attendanceDateModel.state == this.attendanceStateEnum.Attended) return true;
        return false;
    }

    protected isAttendanceDateAbsent(attendanceDateModel: AssignmentParticipantsAttendanceDateModel): boolean {
        if (attendanceDateModel.state == this.attendanceStateEnum.Absent) return true;
        return false;
    }

    protected save(): void {
        if (this.isSaving) return;
        if (this.isLoading) return;

        this.isSaving = true;
        this.isLoading = true;

        let updatedParticipantAttendances = this.getUpdatedParticipantAttendances();
        this.assignmentParticipantsAttendanceService.updateAssignmentParticipantsAttendances(this.assignmentId, updatedParticipantAttendances).subscribe({
            next: () => {
                this.sharedToasterService.showGeneralSavingSuccess();
                this.bsModalRef.hide();
                this.isSaving = false;
            },
            error: (error) => {
                this.sharedToasterService.showGeneralSavingError();
                this.isSaving = false;
                this.isLoading = false;
            }
        });
    }

    private getUpdatedParticipantAttendances(): AssignmentParticipantsAttendancesUpdatePostModel[] {
        let updatedParticipantAttendances: AssignmentParticipantsAttendancesUpdatePostModel[] = [];

        this.participantsAttendances?.forEach(x => {
            x.dates.forEach(d => {
                if (d.state != d.originState) {
                    let newUpdatedParticipantAttendances: AssignmentParticipantsAttendancesUpdatePostModel = {
                        eventLessonId: d.eventLessonId,
                        personId: x.personId,
                        state: d.state
                    };

                    updatedParticipantAttendances.push(newUpdatedParticipantAttendances);
                }
            })
        });

        return updatedParticipantAttendances;
    }


    private getNextState(currentState: AssignmentParticipantAttendanceStateEnum): AssignmentParticipantAttendanceStateEnum {
        switch (currentState) {
            case this.attendanceStateEnum.Undefined:
                return this.attendanceStateEnum.Attended;
            case this.attendanceStateEnum.Attended:
                return this.attendanceStateEnum.Absent;
            default:
                return this.attendanceStateEnum.Undefined;
        }
    }

    private loadParticipantsAttendances(): void {
        this.assignmentParticipantsAttendanceService.getAssignmentParticipantsAttendances(this.assignmentId).subscribe({
            next: (response) => {
                this.processLoadedParticipantsAttendances(response);
            },
            error: (error) => {
                this.sharedToasterService.showGeneralDataLoadingError();
            }
        });
    }

    private processLoadedParticipantsAttendances(assignmentParticipantsAttendances: AssignmentParticipantsAttendancesModel[]): void {
        if (assignmentParticipantsAttendances == null || !assignmentParticipantsAttendances.length || assignmentParticipantsAttendances[0].dates == null || !assignmentParticipantsAttendances[0].dates.length) {
            this.participantsAttendances = [];
            return;
        }

        assignmentParticipantsAttendances.forEach(x => {
            x.dates = x.dates.sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime());

            x.dates.forEach(s => {
                s.originState = s.state;
            });
        });

        // sort by names
        assignmentParticipantsAttendances = assignmentParticipantsAttendances.sort((a, b) => (a.personFirstName + a.personLastName).localeCompare((b.personFirstName + b.personLastName)));

        // assign data
        this.dates = assignmentParticipantsAttendances[0].dates;
        this.participantsAttendances = assignmentParticipantsAttendances;
    }

    private scrollToTop(): void {
        try {
            let result = document.getElementsByClassName("modal-body");
            result[0].scrollTop = 0;
        } catch (err) { }
    }
}
